// React
import { useEffect } from "react"

// Components
import { PrintTransaction } from "components"

// Form
import { useFormContext, useWatch } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"

// Types
import type { GroupName } from "../../types"

// Utils
import { convertNumber, useAccounts, useHandleAuth, useLocation } from "utils"
import { useTrader } from "../../components/widgets/FormSection/components/Trader/utils"
import { type FormType, getAmount } from "../../utils"
import { totalAmountJournal } from "../utils/function"
import { useEmployeeList } from "../utils/hooks"

export function Print(): JSX.Element {
  // Form
  const { control } = useFormContext<FormType>()
  const [card_id, datajournal, group_card, location_id, memo, reference_no, tax_inclusive, transaction_date, trx_code] = useWatch({
    control,
    name: ["card_id", "datajournal", "group_card", "location_id", "memo", "reference_no", "tax_inclusive", "transaction_date", "trx_code"]
  })
  const total = totalAmountJournal(datajournal)
  const balance = total.credit - total.debet

  // Hooks
  const account = useAccounts()
  const location = useLocation()
  const trader = useTrader()
  const employee = useEmployeeList()
  const { userTokenData } = useHandleAuth()

  // Vars
  const isLoading: boolean = account.isLoading || location.isLoading || trader.isLoading
  const selectedLocation = location.data.find(item => item.value === location_id)
  const selectedEmployee = employee.data.find(item => item.employee_id.toString() === card_id)

  useEffect(() => {
    if (Boolean(group_card)) {
      trader.refetch(group_card as GroupName)
    }

    // eslint-disable-next-line
  }, [group_card])

  return (
    <PrintTransaction loading={isLoading}>
      <section className="flex flex-col gap-5">
        <section>
          <div className="bg-[#797979] px-10 py-3 w-fit text-white">TRANSFER MONEY | PAY CHEQUE</div>
          <div className="py-5 border-y font-bold text-center">{userTokenData()?.company_name}</div>
        </section>

        <section className="gap-3 grid grid-cols-2 px-3">
          <InfoSection
            list={[
              {
                label: "Request By",
                value: selectedEmployee?.employee_name ?? "-"
              },
              {
                label: "Location",
                value: selectedLocation?.label ?? "-"
              }
            ]}
          />

          <InfoSection
            list={[
              {
                label: "ID Number",
                value: trx_code
              },
              {
                label: "Transaction Date",
                value: moment(transaction_date).format("DD/MM/YYYY")
              },
              {
                label: "Reference No",
                value: reference_no
              },
            ]}
          />
        </section>

        <table className="table header-bordered">
          <thead>
            <tr>
              <th></th>
              <th>Account Number</th>
              <th>Memo</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Tax</th>
            </tr>
          </thead>

          <tbody>
            {datajournal.map((item, key) => {
              // Vars
              const amount = getAmount(item.amount, true, item.rate, tax_inclusive)

              return (
                <tr key={key}>
                  <td className="text-center">{key + 1}</td>
                  <td>{item.coa_name}</td>
                  <td>{item.memo}</td>
                  <td className="text-right">{item.position === 1 ? convertNumber(amount).intoCurrency : 0}</td>
                  <td className="text-right">{item.position === 2 ? convertNumber(amount).intoCurrency : 0}</td>
                  <td>{item.tax_name}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <section className="gap-3 grid grid-cols-2 px-3">
          <InfoSection
            list={[
              {
                label: "Memo",
                value: memo
              }
            ]}
          />

          <InfoSection
            class={{ value: "text-right" }}
            list={[
              {
                label: "Total Debit",
                value: convertNumber(total.debet).intoCurrency
              },
              {
                label: "Tax Credit",
                value: convertNumber(total.credit).intoCurrency
              },
              {
                label: "Balance",
                value: convertNumber(balance).intoCurrency
              }
            ]}
          />
        </section>
      </section>
    </PrintTransaction>
  )
}

function InfoSection(params: {
  class?: { value?: string }
  list: {
    label: string
    value: string
  }[]
}): JSX.Element {
  return (
    <table className="table border-b h-fit">
      <tbody>
        {params.list.map((item, key) => (
          <tr key={key}>
            <td className="font-bold">{item.label}</td>
            <td className={params?.class?.value}>{item.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}